/** /activities/ — cross-tenant Activity catalog.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). Reads, create and delete; an edit is the activity
 * changeset (`activityRecord.ts`) — the console never PATCHes an activity. A
 * delete of an in-use activity returns 409 `{code: 'activity_in_use'}`.
 */
import { apiDelete, apiGet, apiPost, extendedListConfig } from './client';
import type { NamedRef, TenantRef } from './tours';
import type { components } from './types';
import {
  fetchUsedBy,
  type UsedByParams,
  type UsedByRef,
  type UsedByResponse,
  type UsedByTypeOption,
} from './usedBy';
import type { PaginatedResponse } from './users';
import type { ImageChunks } from '@/utils/imageUrl';
import type { TenantIdsParam } from '@/scope/tenantIds';

/** Activity.type — content discriminator. `5` = attractions. */
export const ACTIVITY_TYPE = {
  THINGS_TO_DO: 1,
  FOOD_AND_DRINK: 2,
  LODGING: 3,
  TRANSPORTATION: 4,
  ATTRACTION: 5,
} as const;
export type ActivityType = (typeof ACTIVITY_TYPE)[keyof typeof ACTIVITY_TYPE];

export const ACTIVITY_TYPE_LABELS: Record<number, string> = {
  1: 'Things to do',
  2: 'Food & drink',
  3: 'Lodging',
  4: 'Transportation',
  5: 'Attraction',
};

/** Activity.directions_type — how the "Get directions" button behaves. */
export const DIRECTIONS_TYPE = { DISABLED: 0, SYSTEM: 1, URL: 2 } as const;
export type DirectionsType = (typeof DIRECTIONS_TYPE)[keyof typeof DIRECTIONS_TYPE];

export const DIRECTIONS_TYPE_LABELS: Record<number, string> = {
  0: 'Disabled',
  1: 'Open in default app',
  2: 'Open custom URL',
};

/** The category, brief form (list rows + detail). */
export interface CategoryBriefRef {
  id: number;
  title: string;
}

/** The detail's category: the brief form plus its icon as the app shows it (the record
 *  header's chip). */
export interface ActivityCategoryRef extends CategoryBriefRef {
  icon: ImageChunks | null;
}

/** The country, brief form (detail). */
export interface CountryBriefRef {
  id: number;
  name: string;
}

/** The currency, full form (detail). */
export interface CurrencyRef {
  id: number;
  code: string;
  symbol: string;
}

export interface ActivityRow {
  id: number;
  /** Owning tenant; null for unassigned/orphaned activities (`Activity.tour_operator`
   *  is nullable). The list grid renders null as '—'. */
  tenant: TenantRef | null;
  name: string;
  display_name: string | null;
  type: ActivityType;
  type_display: string | null;
  category: CategoryBriefRef | null;
  country_name: string | null;
  price_type: number | null;
  price_type_display: string | null;
  latitude: number | null;
  longitude: number | null;
  // Cost/pricing (list serializer): min/max/currency are visible columns; the
  // rest are hidden by default but toggleable.
  cost_min: number | null;
  cost_max: number | null;
  currency: CurrencyRef | null;
  cost_prefix: string | null;
  cost_unit: string | null;
  /** Whether the activity's cost is included, paid on site or a prepaid add-on (0–2). The
   *  list sends the number alone; the grid words it as the detail's `payment_type_display`
   *  does (the search's `PAYMENT_TYPE_LABELS`). */
  payment_type: number | null;
  price_level: number | null;
  address: string | null;
  updated_ts: string | null;
  /** The operator's external identifier (import matching). */
  custom_id: string | null;
  /** Gallery images. Not orderable. */
  image_count: number;
  /** The import source of the newest import that touched it (its name); null = created in
   *  the console. Not orderable. */
  source_name: string | null;
}

// ---------------------------------------------------------------------------
// The record's detail fields (plan §3, §9.5)
// ---------------------------------------------------------------------------

/** A catalog record's newest import (`ConsoleCatalogSource`): its source, when it ran and
 *  whether the source's next sync updates the record. Null on the detail when no import
 *  touched the record (created in the console). Shared by the activity and itinerary
 *  details. */
export interface CatalogSource {
  /** The import source (Import_Source) id. */
  id: number;
  name: string;
  /** That import was an AI import (named by its date). */
  ai_import: boolean;
  /** When that import ran (ISO). */
  last_run_at: string;
  /** The source may update this kind (`allow_import.update_activities` /
   *  `update_itineraries`); false = a create-only source. */
  updates: boolean;
}

/** The linked Google place (read-only). */
export interface ActivityPlace {
  /** Google place id. */
  id: string;
  /** The place on Google Maps. */
  link: string;
}

/** The aggregate rating stored on the activity (`_star_rating`, `_review_count`): the total over
 *  its rating sources (the detail's `rating_count`, its Activity_Ratings rows) — NOT what the app
 *  shows, which is one source's rating. Read as "4.6★ · 2,054 reviews from 3 sources"
 *  (`ratingText`). */
export interface ActivityRatings {
  /** Null without reviews (not a zero rating). */
  star_rating: number | null;
  review_count: number;
}

/** The record's cheap usage counts (D7); tours and their departures come from
 *  `usedBySummary`. */
export interface ActivityUsage {
  /** Catalog itineraries whose days hold it (what refuses the delete) — the used-by's
   *  `demo_itinerary_group` rows. */
  catalog_itineraries: number;
  /** Line-set stops listing it as a nearby attraction. */
  stops: number;
  /** Activities listing it among their nearby activities. */
  nearby_of: number;
  documents: number;
  images: number;
  ratings: ActivityRatings;
}

/** The version cache's cascade drain mode: tour caches refresh in the background (`full`,
 *  `best_effort`) or wait (`paused`). */
export type CascadeMode = 'full' | 'best_effort' | 'paused';

/** How far a saved edit reaches the app (plan §8, D8). */
export interface ActivityAppReach {
  cascade_mode: CascadeMode;
  /** The operator's (or its franchise parent's) `manifest_all_types`: the activity's own app
   *  entry updates in ~3 s. */
  manifest_all_types: boolean;
  /** edit_context only, while the activity's used-by summary is remembered (10 minutes): its
   *  `tours_upcoming`. Never on the detail — the band reads the summary itself. */
  tours_upcoming?: number;
}

export interface ActivityDetail extends ActivityRow {
  /** With its icon (list rows carry the brief form). */
  category: ActivityCategoryRef | null;
  /** `payment_type`'s word ("Cost is included" / "Pay at location" / "Prepaid add-on");
   *  the detail only. */
  payment_type_display: string | null;
  country: CountryBriefRef | null;
  preview_text: string | null;
  /** HTML. */
  description: string | null;
  internal_notes: string | null;
  website: string | null;
  phone: string | null;
  directions_type: number | null;
  directions_url: string | null;
  directions_deep_link: boolean | null;
  min_duration: number | null;
  max_duration: number | null;
  /** Google place id (read-only). */
  place_id: string | null;
  /** Google place data (read-only). */
  place_data: Record<string, unknown> | null;
  opening_hours: unknown[] | null;
  atag: NamedRef[];
  created_ts: string | null;
  /** Documents & links (= `usage.documents`); `image_count` = `usage.images`. */
  document_count: number;
  /** Rating sources (Activity_Ratings rows). */
  rating_count: number;
  email: string | null;
  ext_link: string | null;
  /** IANA zone, derived from the coordinates on save (read-only). */
  timezone: string | null;
  /** The import identity (a UUID derived from `custom_id`; read-only). */
  ext_id: string | null;
  /** The app cache version (read-only). */
  version: number;
  /** Legacy, not shipped to the app and edited nowhere (read-only). */
  notes: string | null;
  /** Legacy, not shipped to the app (read-only). */
  ontrip_description: string | null;
  /** Legacy, not shipped to the app (read-only). */
  description_abbrev: string | null;
  /** The activity in the operator's dashboard; null without an operator. */
  dashboard_url: string | null;
  place: ActivityPlace | null;
  source: CatalogSource | null;
  usage: ActivityUsage;
  /** Its nearby activities, in their order; `name` is the internal name. */
  children: NamedRef[];
  app_reach: ActivityAppReach;
}

export interface ListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  /** One id or a comma list of ids (D1). */
  tour_operator_id?: TenantIdsParam;
  type?: ActivityType;
  category?: number;
  /** URL-encoded JSON array of ActivityFilterNode (the QuerySearch wire AST).
   *  Pre-serialized — the page threads it through ResourceDataGrid's scalar
   *  `extra` channel, so this takes a string (not WireNode[]). */
  filters?: string;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.timeout) usp.set('timeout', params.timeout);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  if (params.type !== undefined) usp.set('type', String(params.type));
  if (params.category !== undefined) usp.set('category', String(params.category));
  if (params.filters) usp.set('filters', params.filters);
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<ActivityRow>> {
  return apiGet<PaginatedResponse<ActivityRow>>(`/activities/${toQuery(params)}`, extendedListConfig(params.timeout));
}

export function retrieve(id: number): Promise<ActivityDetail> {
  return apiGet<ActivityDetail>(`/activities/${id}/`);
}

/** POST /activities/ body: what the create dialog sends. The rest is edited on the
 *  record. */
export interface ActivityWriteJson {
  tour_operator: number;
  name: string;
  type?: ActivityType;
  category?: number | null;
}

/** POST /activities/ — plain JSON. Create needs `tour_operator` + `name`. */
export function create(body: ActivityWriteJson): Promise<ActivityDetail> {
  return apiPost<ActivityDetail>('/activities/', body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/activities/${id}/`);
}

// ---------------------------------------------------------------------------
// Used-by (GET /activities/{id}/used_by/)
// ---------------------------------------------------------------------------

/** The activity used-by's groups, one `type_key` each (the spec's enum): tours whose own
 *  itinerary has it on a day; catalog itineraries whose days have it and orphan day plans
 *  (both refuse the delete); line-set stops listing it as a nearby attraction; activities
 *  listing it among their nearby activities; and travellers' own trips — counted, never
 *  listed (`count_only`), and refusing the delete too. The endpoint runs its rows by
 *  `type_name`. */
export const ACTIVITY_USED_BY_TYPES = [
  'demo_group_tour',
  'demo_itinerary_group',
  'demo_itinerary',
  'demo_vehicle_route_stop',
  'demo_activity',
  'demo_user_itinerary',
] as const;
export type ActivityUsedByType = (typeof ACTIVITY_USED_BY_TYPES)[number];

/** One referencing record. `description` is its name (a parent activity's: the name the app
 *  shows); an orphan day plan has no page (`dashboard_link` / `admin_link` null). */
export interface ActivityUsedByRef extends UsedByRef {
  /** Tours, catalog itineraries, orphan day plans: the first day the activity is on. */
  day?: number | null;
  /** Line-set stops: metres from the stop. */
  distance?: number;
}

export interface ActivityUsedByTypeOption extends UsedByTypeOption {
  key: ActivityUsedByType;
  /** The type's total rows before `search` / `type`; trips at most 1,000 (`capped`). */
  count: number;
}

/** The shared used-by envelope over the activity's groups. `available_types` lists every
 *  type with at least one row, with its count; a count-only type (`demo_user_itinerary`) is
 *  reported there alone — no rows, nothing added to `count`. */
export interface ActivityUsedByList extends UsedByResponse {
  results: ActivityUsedByRef[];
  available_types: ActivityUsedByTypeOption[];
}

/** One page of where the activity is used: rows type by type, then by name; `type` narrows
 *  them to one group. */
export function usedBy(
  id: number,
  params: Omit<UsedByParams, 'type'> & { type?: ActivityUsedByType } = {},
): Promise<ActivityUsedByList> {
  return fetchUsedBy<ActivityUsedByList>('activities', id, params);
}

// ---------------------------------------------------------------------------
// Used-by summary (GET /activities/{id}/used_by/summary/)
// ---------------------------------------------------------------------------

/** The tours using the activity (at most 500 — `capped` above, with no departures counted)
 *  and their upcoming departures and bookings; `available: false` + `reason` when the
 *  database cancelled the read (every count null). Remembered 10 minutes (2 when
 *  unavailable); `computed_at` says when it was read (plan §9.5, D7). */
export type ActivityUsedBySummary = components['schemas']['ConsoleActivityUsedBySummary'];

/** The activity's used-by summary — lazy: the record's band reads it after the detail, which
 *  never computes it. */
export function usedBySummary(id: number): Promise<ActivityUsedBySummary> {
  return apiGet<ActivityUsedBySummary>(`/activities/${id}/used_by/summary/`);
}
